import { inject, Injectable } from "@angular/core";
import { EncounterClosureReason, EncounterFinancials, EncounterStatus, TealCompEncounter, TealCompEncounterHeader, TealCompEncounterUpdate } from "../models/encounter";
import {
    Emails, ApiService, Diaries, Users, UserState, UserService, SignalingService, HarloweSupport, DraftEmail, DraftEmailInsert,
    EncounterDraftEmail, ReceivedEmail, SentEmail, Diary, DiaryInsert, DiaryUpdate, Note, NoteAddendum, NoteAddendumInsert, NoteInsert, NoteUpdate,
    BlockUI,
    MedicalBillInsert,
    ErrorMessage,
    User
} from "@harlowe/core";
import { NotFoundError, Observable, catchError, distinctUntilChanged, filter, firstValueFrom, forkJoin, lastValueFrom, map, of, tap } from "rxjs";
import { environment } from "../../environments/environment";
import { formatDate } from "@angular/common";
import { MessageService } from "primeng/api";
import { HubConnection } from "@microsoft/signalr";
import { Store } from "@ngxs/store";
import { Encounters } from "../state/encounters/encounter-actions";
import { Appointment, AppointmentInsert } from "@harlowe/core";
import { plainToClass, plainToInstance } from 'class-transformer';
import { Fax, FaxWrite } from "@harlowe/core";
import { MedicalBill } from "../models/medical-bill";

export abstract class EncounterServiceBase {
    abstract receiveNewEncounter(encounter: TealCompEncounter): Promise<void>;
    abstract searchForEncounters(filter: EncounterFilter): Promise<Map<number, EncounterSearchResult>>;
    abstract getEncounter(encounterId: number): Observable<TealCompEncounter>;
    abstract getEncounters(encounterId: number[]): Promise<TealCompEncounterHeader[]>;
    abstract updateEncounter(encounterId: number, encounterUpdate: TealCompEncounterUpdate): Promise<TealCompEncounter>;
    abstract getEncounterHeader(encounterId: number): Observable<TealCompEncounterHeader>;
    abstract assignEncounter(encounterId: number, userId: number): Promise<TealCompEncounter>;
    abstract assignEmailsToEncounter(encounterId: number, emails: ReceivedEmail[]): Promise<ReceivedEmail[] | []>;
    abstract getEncounterDiaries(encounterId: number): Observable<Diary[]>;
    abstract getEncounterNotes(encounterId: number): Observable<Note[]>;
    abstract getEncounterSentEmails(encounterId: number): Observable<SentEmail[]>;
    abstract getReceivedNoCategoryEmails(): Observable<ReceivedEmail[]>;
    abstract getEncounterReceivedEmails(encounterId: number): Observable<ReceivedEmail[]>;
    abstract getEncounterUnreadReceivedEmails(): Observable<ReceivedEmail[]>;
    abstract getEncounterUnreadReceivedEmail(emailId: number): Observable<ReceivedEmail>;
    abstract downloadPayerInvoicePackage(encounterId: number): Promise<Blob>;
    abstract emailPayerInvoicePackage(encounterId: number, email: DraftEmail): Promise<SentEmail | undefined>;
    abstract addNewDiary(encounterId: number, diary: DiaryInsert): Promise<Diary>;
    abstract addNewNote(encounterId: number, note: NoteInsert): Promise<Note>;
    abstract updateDiary(encounterId: number, diaryId: number, diary: DiaryUpdate): Promise<Diary>;
    abstract assignDiary(encounterId: number, diaryId: number, userId: number, dueDate?: Date): Promise<Diary>;
    abstract closeDiary(encounterId: number, diaryId: number): Promise<Diary>;
    abstract updateNote(encounterId: number, noteId: number, note: NoteUpdate): Promise<Note>;
    abstract deleteNote(encounterId: number, noteId: number): void;
    abstract addNoteAddendum(encounterId: number, noteId: number, addendum: NoteAddendumInsert): Promise<NoteAddendum>;
    abstract addAppointment(encounterId: number, appointment: AppointmentInsert): Promise<Appointment>;
    abstract cancelAppointment(encounterId: number, appointmentId: number): Promise<Appointment>;
    abstract setMedicalBill(encounterId: number, medicalBill: MedicalBillInsert): Promise<MedicalBill>;
    abstract clearMedicalBill(encounterId: number): Promise<MedicalBill>;
    abstract addAdditionalMedicalBill(encounterId: number, medicalBill: MedicalBillInsert): Promise<MedicalBill>;
    abstract addMedicalBillFromFile(encounterId: number, billImageFile: File): Promise<MedicalBill | undefined>;
    abstract deleteAdditionalMedicalBill(encounterId: number, billId: number): Promise<MedicalBill>;
    abstract lockEncounter(encounterId: number): Promise<TealCompEncounter | undefined>;
    abstract unlockEncounter(encounterId: number): Promise<TealCompEncounter | undefined>;
    abstract searchEncounterFinancials(processedAfter?: Date, processedBefore?: Date, includeComplete?: boolean): Promise<EncounterFinancials[]>;
    abstract processEncounterFinancials(encounterId: number): Promise<EncounterFinancials>;
    abstract unProcessEncounterFinancials(encounterId: number): Promise<EncounterFinancials>;
    abstract sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined>;
    abstract sendEncounterDraftEmail(draft: EncounterDraftEmail): Promise<SentEmail | undefined>;
    abstract addEncounterDraftEmail(encounterId: number, email: DraftEmailInsert): Promise<EncounterDraftEmail | undefined>;
    abstract getEncounterDraftEmails(): Promise<EncounterDraftEmail[]>;
    abstract getEncounterDraftEmail(encounterId: number, emailId: number): Promise<EncounterDraftEmail>;
    abstract sendEncounterFax(encounterId: number, fax: FaxWrite): Promise<Fax>;
    abstract getEncounterFaxes(encounterId: number): Observable<Fax[]>
    abstract addAttachments(encounterId: number, files: FileUpload[]): Promise<HarloweSupport.File[]>;
    abstract deleteAttachment(encounterId: number, fileId: number): Promise<HarloweSupport.File>;
    abstract getEncounterClosureReasons(): Observable<EncounterClosureReason[]>;
    abstract getEncounterStatuses(): Observable<EncounterStatus[]>;
    abstract downloadEncounterDocumentation(encounterId: number): Promise<Blob>;
    abstract closeEncounter(encounterId: number, closureReason: number): Promise<TealCompEncounterHeader | undefined>;
    abstract downloadEncounterFile(encounterId: number, fileName: string): Promise<Blob>;
}


@Injectable({ providedIn: 'root' })
export class EncounterService extends EncounterServiceBase {

    currentUser$ = this._store.select(UserState.currentUser);
    private _encounterHub: HubConnection;


    private _api: ApiService = inject(ApiService);
    private _user: UserService = inject(UserService);
    private _signaling: SignalingService = inject(SignalingService);
    constructor
        (
            private _store: Store,
            private _messages: MessageService
        ) {
        super();
        this._encounterHub = this._signaling.generateHub(environment.harlowe.tealcomp.baseUri + environment.harlowe.tealcomp.signaling.endpoints.encounters);

        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe(cu => {
                if (cu?.id != null) {
                    this._signaling.startHub(this._encounterHub);
                }
                else {
                    this._encounterHub.stop().catch(err => console.error('Hub stop failed', err));
                }
            }
            );
        //Handle inbound hub messages
        this._encounterHub.on("NewEncounterCreated", (encounter: TealCompEncounter) => this.receiveNewEncounter(encounter));

        //When a referral is updated
        this._encounterHub.on("EncounterUpdated", (encounter: TealCompEncounterHeader) => this.encounterUpdated(encounter));
        this._encounterHub.on("EncounterAssigned", (encounter: TealCompEncounterHeader) => this.encounterAssignedUser(encounter)); // TODO: Signal off to both user and ecounter states that encounter has been assigned/unassigned
        this._encounterHub.on("EncounterUnassigned", (encounter: TealCompEncounterHeader) => this.encounterUnassignedUser(encounter));
        this._encounterHub.on("ItemUpdated", (encounter: TealCompEncounter) => this.encounterUpdated(plainToInstance(TealCompEncounter, encounter)));
        this._encounterHub.on("DiaryAdded", (encounterId: number, diary: Diary) => _store.dispatch(new Encounters.DiaryAdded({ encounterId: encounterId, diary: diary })));
        this._encounterHub.on("DiaryUpdated", (encounterId: number, diary: Diary) => this.diaryUpdated(encounterId, diary));
        this._encounterHub.on("NoteAdded", (encounterId: number, note: Note) => _store.dispatch(new Encounters.NoteAdded({ encounterId: encounterId, note: note })));
        this._encounterHub.on("NoteUpdated", (encounterId: number, note: Note) => _store.dispatch(new Encounters.NoteUpdated({ encounterId: encounterId, note: note })));
        this._encounterHub.on("NoteDeleted", (encounterId: number, noteId: number) => _store.dispatch(new Encounters.NoteDeleted({ encounterId: encounterId, noteId: noteId })));
        this._encounterHub.on("NoteAddendumAdded", (encounterId: number, noteId: number, addendum: NoteAddendum) => _store.dispatch(new Encounters.NoteAddendumAdded({ encounterId, noteId, addendum })));
        this._encounterHub.on("AppointmentAdded", (encounterId: number, appointment: Appointment) => _store.dispatch(new Encounters.AppointmentAdded({ encounterId: encounterId, appointment: appointment })));
        this._encounterHub.on("AppointmentCancelled", (encounterId: number, appointment: Appointment) => _store.dispatch(new Encounters.AppointmentCancelled({ encounterId: encounterId, appointment: appointment })));

        //When an email is received
        this._encounterHub.on("EncounterEmailSent", (encounterId: number, email: SentEmail) => _store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: encounterId, email: plainToInstance(SentEmail, email) })));
        this._encounterHub.on("ReceivedEmailLinked", (encounterId: number, email: ReceivedEmail) => _store.dispatch(new Encounters.EncounterServiceEncounterEmailReceived({ encounterId: encounterId, email: plainToInstance(ReceivedEmail, email) })));
        //When an attachment is added
        this._encounterHub.on("AttachmentAdded", (encounterId: number, file: HarloweSupport.File) => _store.dispatch(new Encounters.EncounterServiceAttachmentAdded({ encounterId: encounterId, file: file })));

        this._encounterHub.on("EncounterClosed", (encounter: TealCompEncounterHeader) => _store.dispatch(new Encounters.EncounterServiceEncounterClosed({ encounter: encounter })));

        //When an attachment is deleted
        this._encounterHub.on("AttachmentDeleted", (encounterId: number, file: HarloweSupport.File) => _store.dispatch(new Encounters.EncounterServiceAttachmentDeleted({ encounterId: encounterId, file: file })));

    }


    encounterUpdated(encounter: TealCompEncounter | TealCompEncounterHeader) {

        if (encounter instanceof TealCompEncounter) {
            this.getEncounterHeader(encounter.id)
                .pipe
                (
                    map(h => this._store.dispatch(new Encounters.EncounterUpdated({ header: h, encounter: encounter })))
                )
        }
        else {
            this.getEncounter(encounter.id)
                .pipe
                (
                    map(h => this._store.dispatch(new Encounters.EncounterUpdated({ header: encounter, encounter: h })))
                )
        }

    }

    encounterAssignedUser(encounter: TealCompEncounterHeader) {
        this._store.dispatch(new Encounters.EncounterAssigned({ encounter: encounter }));
        if (encounter.assignedTo)
            this._store.dispatch(new Users.UserAssignedEncounter({ user: encounter.assignedTo }));// In UserState
    }

    encounterUnassignedUser(encounter: TealCompEncounterHeader) {
        this._store.dispatch(new Encounters.EncounterUnassigned({ encounter: encounter }));
        if (encounter.assignedTo)
            this._store.dispatch(new Users.UserUnassignedEncounter({ user: encounter.assignedTo })); // In UserState
    }

    diaryUpdated(encounterId: number, diary: Diary) {
        //Update the date fields in the diary object
        if (diary.dueDate)
            diary.dueDate = new Date(diary.dueDate);
        if (diary.closedDate)
            diary.closedDate = new Date(diary.closedDate);
        diary.createdDate = new Date(diary.createdDate);
        this._store.dispatch(new Encounters.DiaryUpdated({ encounterId: encounterId, diary: diary }));
        this._store.dispatch(new Diaries.EncounterServiceDiaryUpdated({ diaryId: diary.id, diary: diary }))
    }


    override receiveNewEncounter(encounter: TealCompEncounter): Promise<void> {
        throw new Error("Method not implemented.");
    }


    override searchForEncounters(filter: EncounterFilter): Promise<Map<number, EncounterSearchResult>> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<Map<number, { encounter: TealCompEncounterHeader; searchTerms: string[] }>>(environment.harlowe.tealcomp.baseUri, "/encounters/",
                    {
                        ...filter.createdAfter && { createdAfter: formatDate(filter.createdAfter, "MM/dd/yyyy", "en-us") },
                        ...filter.createdBefore && { createdBefore: formatDate(filter.createdBefore, "MM/dd/yyyy", "en-us") },
                        ...filter.processedAfter && { processedAfter: formatDate(filter.processedAfter, "MM/dd/yyyy", "en-us") },
                        ...filter.processedBefore && { processedBefore: formatDate(filter.processedBefore, "MM/dd/yyyy", "en-us") },
                        ...filter.statuses && { statuses: filter.statuses.map(s => s.toString()) },
                        ...filter.ownedByIds && { ownedBy: filter.ownedByIds.map(o => o.toString()) },
                        ...filter.searchTerm && { searchTerm: filter.searchTerm },
                        ...filter.includeUnassigned && { includeUnassigned: filter.includeUnassigned.toString() }
                    }).pipe
                    (
                        catchError((error: NotFoundError) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'No Encounters Found' }); return of(new Map<number, { encounter: TealCompEncounterHeader; searchTerms: string[] }>()); }),
                        map((response: any) => {
                            const resultMap = new Map<number, EncounterSearchResult>();
                            if (response && typeof response === 'object') {
                                Object.entries(response).forEach(([key, value]: [string, any]) => {
                                    const numKey = parseInt(key, 10);
                                    if (!isNaN(numKey) && value && typeof value === 'object') {
                                        resultMap.set(numKey, {
                                            encounter: value.encounter || {} as TealCompEncounterHeader,
                                            searchTerms: Array.isArray(value.searchTerms) ? value.searchTerms : []
                                        });
                                    }
                                });
                            }
                            return resultMap;
                        })
                    )
            )


    }

    override getEncounters(encounterId: number[]): Promise<TealCompEncounterHeader[]> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompEncounterHeader[]>(environment.harlowe.tealcomp.baseUri, "/encounters/list",
                    {
                        encounterIds: encounterId
                    }, {}
                ).pipe
                    (
                        catchError((error: NotFoundError) => { return of([] as TealCompEncounterHeader[]) })
                    )
            )
    }


    override getEncounter(encounterId: number): Observable<TealCompEncounter> {
        return this._api.makeAPIGetCall<TealCompEncounter>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString(), {})
            .pipe
            (
                map(e => {
                    var encounter = plainToClass(TealCompEncounter, e);
                    return encounter;
                }
                )
            )
        // )
    }




    override async updateEncounter(encounterId: number, encounterUpdate: TealCompEncounterUpdate): Promise<TealCompEncounter> {
        await lastValueFrom(this._store.dispatch(new BlockUI.BlockUI()));


        return await lastValueFrom
            (
                this._api.makeAPIPatchCall<TealCompEncounter>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString(), encounterUpdate, {})
                    .pipe
                    (
                        tap(async e => {
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Encounter Updated' });
                            var header = await lastValueFrom(this.getEncounterHeader(e.id));
                            this._store.dispatch(new Encounters.EncounterUpdated({ header: header, encounter: e }))
                            return e;
                        }
                        )
                    )
            )
    }

    override getEncounterHeader(encounterId: number): Observable<TealCompEncounterHeader> {
        // return lastValueFrom
        // (
        return this._api.makeAPIGetCall<TealCompEncounterHeader>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/header", {})
        // )
    }

    override assignEncounter(encounterId: number, userId: number): Promise<TealCompEncounter> {
        var eu = new TealCompEncounterUpdate(encounterId);
        eu.assignedToId = userId;

        return lastValueFrom
            (
                this._api.makeAPIPatchCall<TealCompEncounter>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString(), eu, {})
                    .pipe
                    (
                        map(e => {
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Assigned encounter ' + e.id + ' to ' + e.assignedTo?.fullName });
                            return structuredClone(e);
                        }
                        )
                    )
            )
    }

    override getEncounterDiaries(encounterId: number): Observable<Diary[]> {
        return this._api.makeAPIGetCall<Diary[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/diaries", {})
            .pipe(catchError((error: NotFoundError) => { return of([] as Diary[]) }))
    }

    override getEncounterNotes(encounterId: number): Observable<Note[]> {
        return this._api.makeAPIGetCall<Note[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/notes", {})
            .pipe
            (
                map(notes => notes.map(n => plainToInstance(Note, n))),

                catchError((error: NotFoundError) => { return of([] as Note[]) })
            )
    }

    override getReceivedNoCategoryEmails(): Observable<ReceivedEmail[]> {
        return this._api.makeAPIGetCall<ReceivedEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/emails/received", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(ReceivedEmail, e))),
                catchError((error: NotFoundError) => { return of([] as ReceivedEmail[]) })
            )
    }

    override getEncounterReceivedEmails(encounterId: number): Observable<ReceivedEmail[]> {
        return this._api.makeAPIGetCall<ReceivedEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/emails/received", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(ReceivedEmail, e))),

                catchError((error: NotFoundError) => { return of([] as ReceivedEmail[]) })
            )
    }

    override getEncounterUnreadReceivedEmail(emailId: number): Observable<ReceivedEmail> {
        return this._api.makeAPIGetCall<ReceivedEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + emailId.toString() + "/received/unread", {})
            .pipe
            (
                map(e => { return plainToInstance(ReceivedEmail, e) })
            )
    }

    override getEncounterUnreadReceivedEmails(): Observable<ReceivedEmail[]> {
        return this._api.makeAPIGetCall<ReceivedEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/emails/received/unread", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(ReceivedEmail, e))),

                catchError((error: NotFoundError) => { return of([] as ReceivedEmail[]) })
            )
    }

    override getEncounterSentEmails(encounterId: number): Observable<SentEmail[]> {
        return this._api.makeAPIGetCall<SentEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/emails/sent", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(SentEmail, e))),

                catchError((error: NotFoundError) => { return of([] as SentEmail[]) })
            )
    }

    override sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/emails/send", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: encounterId, email: e }));
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }

    override sendEncounterDraftEmail(draft: EncounterDraftEmail): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + draft.encounterId.toString() + "/emails/drafts/" + draft.id?.toString() + "/send", {})
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: draft.encounterId, email: e }));
                                if (draft.id) this._store.dispatch(new Emails.EmailServiceEmailDraftSent({ draftEmailId: draft.id }))
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }

    override addNewDiary(encounterId: number, diary: DiaryInsert): Promise<Diary> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Diary>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/diary", diary)
                    .pipe
                    (
                        map
                            (
                                d => {
                                    this._store.dispatch(new Diaries.EncounterServiceDiaryCreated({ diaryId: d.id, diary: d }));
                                    return d;
                                }
                            )
                    )
            )
    }
    override addNewNote(encounterId: number, note: NoteInsert): Promise<Note> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Note>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/note", note)
            )
    }
    override updateDiary(encounterId: number, diaryId: number, diary: DiaryUpdate): Promise<Diary> {
        return lastValueFrom
            (
                this._api.makeAPIPatchCall<Diary>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/diary/" + diaryId.toString(), diary)
            )
    }



    override updateNote(encounterId: number, noteId: number, note: NoteUpdate): Promise<Note> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Note>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString(), note)
            )
    }

    override async deleteNote(encounterId: number, noteId: number ): Promise<void> {
        const obs$ = this._api.makeAPIDeleteCall<Note>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString())
        await firstValueFrom(obs$);
    }

    override addNoteAddendum(encounterId: number, noteId: number, addendum: NoteAddendumInsert): Promise<NoteAddendum> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<NoteAddendum>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString() + "/addendum", addendum)
            )
    }

    override async assignDiary(encounterId: number, diaryId: number, userId: number, dueDate?: Date): Promise<Diary> {
        var du = new DiaryUpdate(diaryId);
        du.assignedToId = userId;
        if (dueDate != null)
            du.dueDate = dueDate;
        var diary = await this.updateDiary(encounterId, diaryId, du);
        this._store.dispatch(new Diaries.EncounterServiceDiaryAssigned({ diaryId: diaryId, userId: userId }));

        return diary;
    }

    override async closeDiary(encounterId: number, diaryId: number): Promise<Diary> {
        var du = new DiaryUpdate(diaryId);
        du.closedDate = new Date();
        var currentUser = this._store.selectSnapshot(UserState.currentUser);
        du.closedById = currentUser?.id;
        var diary = await this.updateDiary(encounterId, diaryId, du)
        this._store.dispatch(new Diaries.EncounterServiceDiaryClosed({ diaryId: diaryId }));
        return diary;
    }


    override addAppointment(encounterId: number, appointment: AppointmentInsert): Promise<Appointment> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Appointment>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/appointment", appointment, {})
                    .pipe
                    (
                        map
                            (
                                a => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Encounter Updated' });
                                    this._store.dispatch(new Encounters.AppointmentAdded({ encounterId: encounterId, appointment: a }));
                                    return a;
                                }
                            )
                    )
            )
    }

    override cancelAppointment(encounterId: number, appointmentId: number): Promise<Appointment> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<Appointment>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/appointment/" + appointmentId.toString() + "/cancel", {})
                    .pipe
                    (
                        map
                            (
                                a => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Encounter Cancelled' })
                                    this._store.dispatch(new Encounters.AppointmentCancelled({ encounterId: encounterId, appointment: a }));
                                    return a;
                                }
                            )
                    )
            )
    }

    override setMedicalBill(encounterId: number, medicalBill: MedicalBillInsert): Promise<MedicalBill> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/medicalbill", medicalBill)
                    .pipe
                    (
                        map
                            (
                                bill => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical Bill Set For Encounter' })
                                    this._store.dispatch(new Encounters.MedicalBillSet({ encounterId: encounterId, medicalBill: bill }));
                                    return bill;
                                }
                            )
                    )
            )
    }

    override addMedicalBillFromFile(encounterId: number, billImageFile: File): Promise<MedicalBill | undefined> {
        var d: FormData = new FormData();
        d.append('billImageFile', billImageFile);

        return lastValueFrom
            (
                this._api.makeAPIPutCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/medicalbill/fromImage", d)
                    .pipe
                    (
                        map
                            (
                                bill => {
                                    this._store.dispatch(new Encounters.MedicalBillAdded({ encounterId: encounterId, medicalBill: bill }))
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical bill added from image' })
                                    return bill;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: error.message }); return of(undefined) })
                    )
            )

    }


    override addAdditionalMedicalBill(encounterId: number, medicalBill: MedicalBillInsert): Promise<MedicalBill> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/medicalbill/additional", medicalBill)
                    .pipe
                    (
                        map
                            (
                                bill => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Medical Bill Added For Encounter' })
                                    this._store.dispatch(new Encounters.MedicalBillAdded({ encounterId: encounterId, medicalBill: bill }));
                                    return bill;
                                }
                            )
                    ))
    }




    override clearMedicalBill(encounterId: number): Promise<MedicalBill> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/medicalbill")
                    .pipe
                    (
                        map
                            (
                                bill => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Removed Medical Bill From Encounter' })
                                    this._store.dispatch(new Encounters.MedicalBillRemoved({ encounterId: encounterId, medicalBill: bill }));
                                    return bill;
                                }
                            )
                    )
            )
    }

    override deleteAdditionalMedicalBill(encounterId: number, billId: number): Promise<MedicalBill> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<MedicalBill>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/medicalbill/" + billId.toString())
                    .pipe
                    (
                        map
                            (
                                bill => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Removed Medical Bill From Encounter' })
                                    this._store.dispatch(new Encounters.AdditionalMedicalBillRemoved({ encounterId: encounterId, medicalBill: bill }));
                                    return bill;
                                }
                            )
                    )
            )
    }


    override lockEncounter(encounterId: number): Promise<TealCompEncounter | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompEncounter>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/lock", {})
                    .pipe
                    (
                        map
                            (
                                encounter => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Encounter Locked' })
                                    this._store.dispatch(new Encounters.EncounterLocked({ encounter: encounter }))
                                    return encounter
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Lock Encounter', detail: ' ' + error.message }); return of(undefined) })
                    )
            )
    }


    override unlockEncounter(encounterId: number): Promise<TealCompEncounter | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompEncounter>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/unlock", {})
                    .pipe
                    (
                        map
                            (
                                encounter => {
                                    this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Encounter Unlocked' })
                                    this._store.dispatch(new Encounters.EncounterUnlocked({ encounter: encounter }))
                                    return encounter
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Lock Encounter', detail: ' ' + error.message }); return of(undefined) })
                    )
            )
    }

    override processEncounterFinancials(encounterId: number): Promise<EncounterFinancials> {

        return lastValueFrom
            (
                this._api.makeAPIPostCall<EncounterFinancials>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/finances", {})
                    .pipe
                    (
                        map
                            (
                                ef => {
                                    this._store.dispatch(new Encounters.EncounterServiceEncounterFinancialsPosted({ financials: ef }));
                                    return ef;
                                }
                            ),
                    )
            )
    }

    override unProcessEncounterFinancials(encounterId: number): Promise<EncounterFinancials> {

        return lastValueFrom
            (
                this._api.makeAPIPostCall<EncounterFinancials>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/finances/unpost", {})
                    .pipe
                    (
                        map
                            (
                                ef => {
                                    this._store.dispatch(new Encounters.EncounterServiceEncounterFinancialsUnPosted({ financials: ef }));
                                    return ef;
                                }
                            ),
                    )
            )
    }

    override searchEncounterFinancials(processedAfter?: Date, processedBefore?: Date, includeComplete?: boolean): Promise<EncounterFinancials[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<EncounterFinancials[]>(environment.harlowe.tealcomp.baseUri, "/encounters/finances",
                    {
                        ...processedAfter && { processedAfter: formatDate(processedAfter, "MM/dd/yyyy", "en-us") },
                        ...processedBefore && { before: formatDate(processedBefore, "MM/dd/yyyy", "en-us") },
                        ...includeComplete && { includedInvoiced: includeComplete.toString() }
                    })
            )
    }

    override addAttachments(encounterId: number, files: FileUpload[]): Promise<HarloweSupport.File[]> {
        return lastValueFrom
            (
                forkJoin
                    (
                        files.map
                            (
                                f => {
                                    var d: FormData = new FormData();
                                    d.append('file', f);
                                    if (f.category)
                                        d.append('category', f.category)
                                    if (f.fileType)
                                        d.append('fileType', f.fileType)

                                    return this._api.makeAPIPutCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/attachments", d, {})
                                }
                            )
                    )
                    .pipe
                    (
                        map
                            (
                                f => {
                                    this._store.dispatch(new Encounters.EncounterServiceAttachmentsAdded({ encounterId: encounterId, files: f }));
                                    return f;
                                }
                            )
                    )
            )
    }

    override deleteAttachment(encounterId: number, fileId: number): Promise<HarloweSupport.File> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<HarloweSupport.File>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/attachments/" + fileId.toString(), {})

                    .pipe
                    (
                        map
                            (
                                f => {
                                    this._store.dispatch(new Encounters.EncounterServiceAttachmentDeleted({ encounterId: encounterId, file: f }));
                                    return f;
                                }
                            )
                    )
            )
    }



    override getEncounterStatuses(): Observable<EncounterStatus[]> {
        return this._api.makeAPIGetCall<EncounterStatus[]>(environment.harlowe.tealcomp.baseUri, "/encounters/statuses", {});
    }


    override getEncounterClosureReasons(): Observable<EncounterClosureReason[]> {
        return this._api.makeAPIGetCall<EncounterClosureReason[]>(environment.harlowe.tealcomp.baseUri, "/encounters/closureReasons", {});
    }

    override closeEncounter(encounterId: number, closureReason: number): Promise<TealCompEncounterHeader | undefined> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealCompEncounterHeader>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/close", {},
                    {
                        ...closureReason && { closureReason: closureReason.toString() }
                    })
                    .pipe
                    (
                        map
                            (
                                r => {
                                    this._messages.add({ key: "main-alert", severity: 'success', summary: 'Encounter Closed' });
                                    return r;
                                }
                            ),
                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Close Encounter', detail: error.message }); return of() })
                    )
            );

    }

    override downloadEncounterDocumentation(encounterId: number): Promise<Blob> {
        return lastValueFrom
            (
                this._api.getFileBlob(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/documentation/download")
            );
    }

    override downloadPayerInvoicePackage(encounterId: number): Promise<Blob> {
        return lastValueFrom
            (
                this._api.getFileBlob(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/payerinvoicepackage/download")
            );
    }

    override emailPayerInvoicePackage(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/payerinvoicepackage/email", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Invoice Email Sent' });
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }

    override getEncounterDraftEmail(encounterId: number, emailId: number): Promise<EncounterDraftEmail> {
        return lastValueFrom(this._api.makeAPIGetCall<EncounterDraftEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/emails/drafts/" + emailId.toString(), {})
            .pipe
            (
                map
                    (
                        draft => {
                            return plainToClass(EncounterDraftEmail, draft);
                        }
                    )
            ));
    }

    override getEncounterDraftEmails(): Promise<EncounterDraftEmail[]> {
        return lastValueFrom(this._api.makeAPIGetCall<EncounterDraftEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/emails/drafts", {})
            .pipe
            (
                map
                    (
                        drafts => drafts.map(draft => plainToInstance(EncounterDraftEmail, draft))
                    )
            ));
    }

    override addEncounterDraftEmail(encounterId: number, email: EncounterDraftEmail): Promise<EncounterDraftEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPutCall<EncounterDraftEmail>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/draft", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Saved! You can access it from the email sidebar.' });
                                this._store.dispatch(new Emails.EmailServiceEncounterEmailDraftAdded({ draftEmail: e }));
                                return plainToClass(EncounterDraftEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Draft Email', detail: ' ' + error.message }); return of(undefined) })
                )
        )
    }



    override assignEmailsToEncounter(encounterId: number, emails: ReceivedEmail[]): Promise<ReceivedEmail[] | []> {
        return lastValueFrom
            (
                this._api.makeAPIPatchCall<ReceivedEmail[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/emails/received", emails)
                    .pipe
                    (
                        map(emails => emails.map(e => plainToInstance(ReceivedEmail, e))),

                        catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Assign Emails to Encounter', detail: ' ' + error.message }); return of([] as ReceivedEmail[]) })
                    )
            )
    }

    override getEncounterFaxes(encounterId: number): Observable<Fax[]> {
        return this._api.makeAPIGetCall<Fax[]>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/faxes", {})
            .pipe(
                map(faxes => faxes.map(fax => plainToInstance(Fax, fax)))
            );
    }

    override sendEncounterFax(encounterId: number, fax: FaxWrite): Promise<Fax> {
        return lastValueFrom(this._api.makeAPIPostCall<Fax>(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString() + "/fax", fax)
            .pipe
            (
                map
                    (
                        ef => {
                            //this._store.dispatch(new Encounters.EncounterService));
                            return ef;
                        }
                    ),
            )
        )
    }

    override downloadEncounterFile(encounterId: number, fileName: string): Promise<Blob> {
        return lastValueFrom(this._api.getFileBlob(environment.harlowe.tealcomp.baseUri, "/encounters/" + encounterId.toString(), { fileName }));
    }
}


export class EncounterFilter {
    statuses: number[] = [];
    ownedByIds: number[] = [];
    createdBefore?: Date;
    createdAfter?: Date;
    processedBefore?: Date;
    processedAfter?: Date;
    searchTerm?: string;
    includeUnassigned: boolean = false;

}

export class EncounterSearchResult {
    encounter?: TealCompEncounterHeader;
    searchTerms: string[] = [];
}


export class FileUpload extends File {
    category?: string;
    fileType?: string;
}